/**
 * Timezone source for the Location dialog's strict IANA picker (DNKADM-0077).
 *
 * The Location `timezone` governs the location's operating-hours/slot math (§4.5),
 * so the dialog constrains the field to a real IANA zone instead of free text. The
 * zone list comes from the **browser-native** `Intl.supportedValuesOf('timeZone')`
 * — always current, zero dependency — and degrades to a small US-centric fallback
 * if that API is somehow absent so the field is never empty/unusable.
 *
 * No data is migrated here: a legacy/invalid stored value is preserved by the
 * dialog (unioned into its options) so an existing record still loads and the
 * admin can correct it — see `LocationDialog`.
 */

/**
 * A minimal IANA fallback used only when `Intl.supportedValuesOf` is unavailable
 * (a handful of common US zones + `UTC`) — enough to keep the picker usable.
 */
const FALLBACK_TIME_ZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'America/Phoenix',
  'Pacific/Honolulu',
];

/** A safe default when the browser can't resolve a zone. */
const DEFAULT_TIME_ZONE = 'UTC';

/** Computed once — the canonical zone list is stable for the session. */
let cachedZones: string[] | null = null;

/**
 * Read `Intl.supportedValuesOf` defensively without depending on its lib typing
 * (it may be absent from the configured TS lib / an older runtime).
 */
function getSupportedValuesOf():
  | ((key: 'timeZone') => string[])
  | undefined {
  const fn = (
    Intl as typeof Intl & {
      supportedValuesOf?: (key: string) => string[];
    }
  ).supportedValuesOf;
  return typeof fn === 'function'
    ? (fn as (key: 'timeZone') => string[])
    : undefined;
}

/**
 * The canonical IANA zone list (sorted), memoized. Sourced from
 * `Intl.supportedValuesOf('timeZone')` when available; otherwise the minimal
 * {@link FALLBACK_TIME_ZONES}. Never empty.
 */
export function listTimeZones(): string[] {
  if (cachedZones !== null) return cachedZones;
  const supportedValuesOf = getSupportedValuesOf();
  let zones: string[] | undefined;
  if (supportedValuesOf) {
    try {
      const values = supportedValuesOf('timeZone');
      if (Array.isArray(values) && values.length > 0) zones = [...values].sort();
    } catch {
      zones = undefined;
    }
  }
  cachedZones = zones ?? [...FALLBACK_TIME_ZONES];
  return cachedZones;
}

/**
 * The browser's resolved IANA zone (e.g. `America/Chicago`) to default a NEW
 * location, guarded to {@link DEFAULT_TIME_ZONE} if the runtime can't resolve one.
 * Not memoized — it's cheap and reflects the live environment.
 */
export function resolvedTimeZone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof tz === 'string' && tz.length > 0 ? tz : DEFAULT_TIME_ZONE;
  } catch {
    return DEFAULT_TIME_ZONE;
  }
}

/** Test-only: drop the memoized zone list so a fallback path can be exercised. */
export function __resetTimeZonesCache(): void {
  cachedZones = null;
}
